import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import type { Listing } from "@/types";
import { DemoBadge } from "@/components/Doodles";

export function EtsyPanel({ listing, demo, onChange }: { listing: Listing; demo: boolean; onChange: (l: Listing) => void }) {
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/etsy-draft`);
      onChange(data);
      toast.success(data.etsy?.demo ? "Demo draft created (no Etsy keys yet)" : "Draft sent to Etsy. Tap publish in Etsy.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (listing.status === "sent" && listing.etsy) {
    return (
      <div className="rounded-2xl border-2 border-[#23324A] bg-[#9CC9E8]/40 p-4 text-sm text-[#23324A]" data-testid="etsy-sent-panel">
        <div className="mb-1 flex items-center gap-2 font-bold">Etsy draft {listing.etsy.demo && <DemoBadge />}</div>
        <p>Draft ID: <span className="font-mono" data-testid="etsy-draft-id">{listing.etsy.listing_id}</span></p>
        {listing.etsy.warning && <p className="mt-1 text-xs font-semibold text-[#C2412D]" data-testid="etsy-warning">{listing.etsy.warning}</p>}
        {listing.etsy.url ? (
          <a href={listing.etsy.url} target="_blank" rel="noreferrer" className="font-bold underline" data-testid="etsy-draft-link">Open in Etsy to publish</a>
        ) : (
          <p className="mt-1 text-xs text-[#23324A]/70">Demo mode: nothing was sent. Add your Etsy API keys to send real drafts.</p>
        )}
      </div>
    );
  }
  if (listing.status !== "approved") return null;
  return (
    <button onClick={send} disabled={busy} className="btn-navy w-full justify-center" data-testid="send-etsy-draft-btn">
      <Send className="h-4 w-4" strokeWidth={2.5} /> {busy ? "Sending…" : "Send to Etsy as draft"} {demo && <DemoBadge />}
    </button>
  );
}
